
import React, { useState, useEffect } from 'react';

export default function IssueReview({ checklistData, damageReports, roomData, onComplete, onRework }) {
  const [aiFlags, setAiFlags] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [scoring, setScoring] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    // AI Simulation: Analyze checklist and reports
    const flags = [];
    if (checklistData && Object.keys(checklistData).length < 8) {
      flags.push({ type: 'Incomplete cleaning', severity: 'Major' });
    }
    if (damageReports.some(r => r.category === 'Damage')) {
      flags.push({ type: 'Missing item reported', severity: 'Damage' });
    }
    if (damageReports.some(r => r.issue?.toLowerCase().includes('stain'))) {
      flags.push({ type: 'Stains detected', severity: 'Minor' });
    }
    if (checklistData && !checklistData['Bedline']) {
      flags.push({ type: 'Unmade bed', severity: 'Minor' });
    }
    if (damageReports.some(r => r.category === 'Major')) {
      flags.push({ type: 'Major damage reported', severity: 'Major' });
    }
    setAiFlags(flags);

    // Calculate score
    let score = 100;
    score -= flags.filter(f => f.severity === 'Minor').length * 5;
    score -= flags.filter(f => f.severity === 'Major').length * 15;
    score -= flags.filter(f => f.severity === 'Damage').length * 25;
    setScoring(Math.max(0, score));
    setIsLoading(false);
  }, [checklistData, damageReports]);

  const handleSendFeedback = (decision) => {
    setFeedback(decision);
    if (decision === 'Approved') {
      setTimeout(() => onComplete(), 500);
    } else if (decision === 'Rework') {
      setTimeout(() => onRework(), 500);
    }
  };

  const getScoreColor = () => {
    if (scoring >= 90) return 'text-green-600';
    if (scoring >= 70) return 'text-blue-600';
    if (scoring >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreMessage = () => {
    if (scoring >= 90) return 'Excellent - Ready for guest occupancy';
    if (scoring >= 70) return 'Good - Minor issues need attention';
    if (scoring >= 50) return 'Fair - Requires additional cleaning';
    return 'Poor - Immediate action required';
  };

  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'Minor': return 'bg-blue-100 text-blue-700';
      case 'Major': return 'bg-yellow-100 text-yellow-700';
      case 'Damage': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-white px-4 sm:px-6 lg:px-8 py-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg border border-blue-100 p-6 mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                AI Inspection Review
              </h1>
              <p className="text-gray-500 mt-1">Automated quality check and analysis</p>
            </div>
            <div className="bg-blue-50 rounded-xl px-4 py-2 border border-blue-200">
              <span className="text-blue-700 font-semibold">
                Room: {roomData?.roomId || roomData?.room || 'Not allocated'}
              </span>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="bg-white rounded-2xl shadow-lg border border-blue-100 p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mb-4"></div>
            <p className="text-gray-500">Analyzing inspection data...</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* AI Flags Card */}
              <div className="bg-white rounded-2xl shadow-lg border border-blue-100 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center shadow-md">
                    <span className="text-white text-xl">🤖</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800">AI Flags Detected</h3>
                </div>
                
                {aiFlags.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <div className="text-5xl mb-3">✅</div>
                    <p className="text-green-600 font-medium">No issues flagged by AI</p>
                    <p className="text-sm text-gray-400 mt-1">Room meets quality standards</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {aiFlags.map((flag, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100">
                        <div className="flex items-center gap-3">
                          <span className="text-xl">
                            {flag.severity === 'Minor' && '⚠️'}
                            {flag.severity === 'Major' && '🔧'}
                            {flag.severity === 'Damage' && '💔'}
                          </span>
                          <span className="text-gray-700 font-medium">{flag.type}</span>
                        </div>
                        <span className={`text-xs px-3 py-1 rounded-full font-semibold ${getSeverityColor(flag.severity)}`}>
                          {flag.severity}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Cleanliness Score Card */}
              <div className="bg-white rounded-2xl shadow-lg border border-blue-100 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-md">
                    <span className="text-white text-xl">📊</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800">Cleanliness Score</h3>
                </div>
                
                <div className="text-center">
                  <div className={`text-6xl font-bold ${getScoreColor()} mb-2`}>
                    {scoring}%
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 mt-3">
                    <div 
                      className={`h-3 rounded-full transition-all duration-500 ${
                        scoring >= 90 ? 'bg-green-500' : 
                        scoring >= 70 ? 'bg-blue-500' : 
                        scoring >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                      }`} 
                      style={{ width: `${scoring}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-600 mt-3 font-medium">
                    {getScoreMessage()}
                  </p>
                  <div className="mt-4 flex justify-center gap-4 text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span>90-100% Excellent</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span>70-89% Good</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                      <span>50-69% Fair</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-blue-100 p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-md">
                  <span className="text-white text-xl">⚡</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-800">Action Required</h3>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button 
                  onClick={() => handleSendFeedback('Approved')} 
                  className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-3 px-4 rounded-xl font-semibold hover:shadow-lg transition-all transform hover:scale-105"
                >
                  ✅ Approved - Mark for Occupancy
                </button>
                <button 
                  onClick={() => handleSendFeedback('Rework')} 
                  className="flex-1 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white py-3 px-4 rounded-xl font-semibold hover:shadow-lg transition-all transform hover:scale-105"
                >
                  🔄 Rework - Rec lean Required
                </button>
                <button 
                  onClick={() => handleSendFeedback('Escalate')} 
                  className="flex-1 bg-gradient-to-r from-red-500 to-red-600 text-white py-3 px-4 rounded-xl font-semibold hover:shadow-lg transition-all transform hover:scale-105"
                >
                  ⚠️ Escalate to Supervisor
                </button>
              </div>
              
              {feedback && (
                <div className="mt-4 p-4 bg-blue-50 rounded-xl text-center border border-blue-200 animate-fade-in">
                  <p className="text-blue-800 font-medium">
                    ✅ Feedback sent: <strong>{feedback}</strong>
                  </p>
                  <p className="text-sm text-blue-600 mt-1">
                    Staff notified at {new Date().toLocaleTimeString()}
                  </p>
                </div>
              )}
            </div>

            {/* Staff Updates Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-blue-100 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center shadow-md">
                  <span className="text-white text-xl">📋</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-800">Staff Updates & Findings</h3>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                  <span className="text-gray-600">Room ID:</span>
                  <span className="font-mono font-semibold text-gray-800">
                    {roomData?.roomId || roomData?.room || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                  <span className="text-gray-600">Areas Inspected:</span>
                  <span className="font-semibold text-gray-800">
                    {Object.keys(checklistData || {}).length}/11
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                  <span className="text-gray-600">Damage Reports:</span>
                  <span className="font-semibold text-gray-800">{damageReports.length}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                  <span className="text-gray-600">Final Status:</span>
                  <span className={`font-semibold ${scoring >= 70 ? 'text-green-600' : 'text-orange-600'}`}>
                    {scoring >= 70 ? 'Ready for approval' : 'Needs review'}
                  </span>
                </div>
              </div>

              {/* Additional Info */}
              {damageReports.length > 0 && (
                <div className="mt-4 p-3 bg-red-50 rounded-xl border border-red-200">
                  <p className="text-sm text-red-700">
                    ⚠️ {damageReports.length} damage report(s) require attention before final approval
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}